import { useQuery } from '@tanstack/react-query';
import { usePublisherLookup } from '../../hooks/useLookups';
import { apiClient } from '../../lib/api';
import { useEffect, useState } from 'react';
import { formatRelativeTime } from '../../lib/utils';
import { CheckCircle2, XCircle, PauseCircle, PlayCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

interface PublisherData {
  id: number;
  name: string;
  subId: string | null;
  isActive: boolean;
  createdAt: number;
}

export default function SourceHeader({
  publisherId,
  subId,
  range,
  customFrom,
  customTo,
}: {
  publisherId: number | null | undefined;
  subId: string | null;
  range?: 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'custom';
  customFrom?: string;
  customTo?: string;
}) {
  const publisherLookup = usePublisherLookup();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Show toast briefly
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Fetch publishers list when linked & drawer open
  const publishersQuery = useQuery({
    queryKey: ['publishers-list'],
    queryFn: () => apiClient.get<PublisherData[]>('/api/v1/publishers'),
    enabled: publisherId !== undefined && publisherId !== null,
  });

  const publisherData = (() => {
    if (publisherId == null) return null;
    return publishersQuery.data?.find((p) => p.id === publisherId) ?? null;
  })();

  const isLinked = publisherId !== undefined && publisherId !== null;
  const isActive = publisherData?.isActive ?? false;
  const publisherName = isLinked ? publisherLookup.getName(publisherId) : null;
  const subIdHint = subId || null;

  const handleToggleStatus = async () => {
    if (!isLinked || !publisherId) return;

    const newStatus = !isActive;
    try {
      await apiClient.patch(`/api/v1/publishers/${publisherId}`, {
        isActive: newStatus,
      });
      queryClient.invalidateQueries({ queryKey: ['publishers-list'] });
      setToastMessage(
        `Publisher ${newStatus ? 'activated' : 'paused'} successfully`
      );
    } catch (error) {
      setToastMessage(`Failed to update status: ${(error as Error).message}`);
    }
  };

  // Render unlinked header
  if (!isLinked) {
    return (
      <>
        <div className="border-b border-border-subtle pb-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-sm uppercase tracking-widest text-neon-cyan">
                Unlinked Conversions
              </h3>
              {subIdHint && (
                <p className="text-[10px] font-mono text-text-tertiary mt-0.5">
                  Sub ID hint: {subIdHint}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-[10px] font-mono font-bold uppercase tracking-wider bg-text-tertiary/20 text-text-tertiary">
                <XCircle size={12} className="mr-1" />
                Unlinked
              </span>
            </div>
          </div>
        </div>
        {toastMessage && (
          <div className="mb-3 p-2.5 bg-neon-yellow/10 border border-neon-yellow/40 rounded-sm">
            <p className="text-xs font-mono text-neon-yellow">{toastMessage}</p>
          </div>
        )}
      </>
    );
  }

  // Render linked header
  return (
    <>
      <div className="border-b border-border-subtle pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-display text-sm uppercase tracking-widest text-neon-cyan truncate">
                {publisherName}
              </h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase tracking-wider bg-neon-green/20 text-neon-green">
                <CheckCircle2 size={12} className="mr-1" />
                Active
              </span>
            </div>
            <p className="text-[10px] font-mono text-text-tertiary mt-0.5">
              Publisher ID: {publisherId}
              {subId && (
                <>
                  {' • '}
                  Sub ID: {subId}
                </>
              )}
            </p>
          </div>

          {/* Status toggle button */}
          <button
            type="button"
            onClick={handleToggleStatus}
            disabled={!isLinked}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider transition-colors ${
              isActive
                ? 'bg-neon-yellow/20 text-neon-yellow hover:bg-neon-yellow/30 border border-neon-yellow/40'
                : 'bg-text-tertiary/20 text-text-tertiary hover:bg-text-tertiary/30 border border-text-tertiary/40'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={isActive ? 'Pause this publisher' : 'Activate this publisher'}
          >
            {isActive ? (
              <>
                <PauseCircle size={14} />
                Pause
              </>
            ) : (
              <>
                <PlayCircle size={14} />
                Activate
              </>
            )}
          </button>
        </div>
      </div>

      {toastMessage && (
        <div className="mb-3 p-2.5 bg-neon-yellow/10 border border-neon-yellow/40 rounded-sm">
          <p className="text-xs font-mono text-neon-yellow">{toastMessage}</p>
        </div>
      )}
    </>
  );
}
